import { toInteger } from 'lodash'

import { formatTime } from '@/util/formatters/time'

import type { DrillDownCustomer } from '../resources'

import type { FrameAgreementData, ReportEntity, ReportEntityWithQuantity } from './types'

export const getMonthOptions = (year: string, locale: string) =>
  Array.from(
    {
      length:
        toInteger(year) === toInteger(new Date().getFullYear()) ? new Date().getMonth() + 1 : 12,
    },
    (v, i) => ({
      value: String(i + 1),
      label: new Date(2020, i, 1).toLocaleString(locale, { month: 'long' }),
    })
  )

export const getQuantity = (
  item: { quantity: number; peakVolumeDiff: number; priceType: 'T' | 'D' | 'Q' | 'O' | 'S' },
  translate: (id: string, values?: Record<string, string>) => string
) => {
  switch (item.priceType) {
    case 'T':
      return translate('{quantity} qty - {peakVolume}', {
        quantity: String(Math.round(item.quantity)),
        peakVolume: formatTime(item.peakVolumeDiff),
      })
    case 'D':
      return `${bytesToMegaBytes(item.peakVolumeDiff)} MB`
    case 'Q':
    case 'O':
    case 'S':
      return `${Math.round(item.quantity)}`
    default:
      return `${item.quantity}`
  }
}

export const bytesToMegaBytes = (bytes: number) => Number(bytes / 1024 / 1024).toFixed(2)

// Example of DrillDownCustomer:
// {
//   "customerId": 1000639,
//   "customerName": "Kunde Norge 1",
//   "customerHeadId": 1,
//   "customerHeadName": "TCM AS",
//   "customerHeadFrameAgreementId": 1,
//   "customerHeadFrameAgreementName": "Oddy's Frame Agreement",
//   "countryId": 47,
//   "countryName": "Norway",
//   "currency": "NOK",
//   "amount": "46373.34",
//   "salaryDeductionAmount": "20285.82",
//   "customerOrgNo": "988888888"
// },

export const formatOrgData = (data: DrillDownCustomer[]): FrameAgreementData => {
  const [first] = data
  const frameAgreementData: FrameAgreementData = {
    id: first.customerHeadFrameAgreementId,
    name: first.customerHeadFrameAgreementName,
    values: new Map(),
    customerHeads: new Map(),
  }

  data.forEach(item => {
    const amount = Number(item.amount)

    // Update frame agreement values
    const currentFrameAgreementValue = frameAgreementData.values.get(item.currency) || 0
    frameAgreementData.values.set(item.currency, currentFrameAgreementValue + amount)

    // Update customer head values
    if (!frameAgreementData.customerHeads.has(item.customerHeadId)) {
      frameAgreementData.customerHeads.set(item.customerHeadId, {
        id: item.customerHeadId,
        name: item.customerHeadName,
        values: new Map(),
        customers: new Map(),
      })
    }

    const customerHead = frameAgreementData.customerHeads.get(item.customerHeadId)!
    const currentCustomerHeadValue = customerHead.values.get(item.currency) || 0
    customerHead.values.set(item.currency, currentCustomerHeadValue + amount)

    // Update customer values
    if (!customerHead.customers.has(item.customerId)) {
      customerHead.customers.set(item.customerId, {
        id: item.customerId,
        name: item.customerName,
        orgNo: item.customerOrgNo,
        values: new Map(),
      })
    }

    const customer = customerHead.customers.get(item.customerId)!
    const currentCustomerValue = customer.values.get(item.currency) || 0
    customer.values.set(item.currency, currentCustomerValue + amount)
  })

  return frameAgreementData
}

export const extractCurrencies = (data: ReportEntity[]) => {
  const currencies = new Set<string>()

  data.forEach(item => {
    currencies.add(item.currency)
  })

  return Array.from(currencies)
}

export const quantityText = (
  item: Pick<ReportEntityWithQuantity, 'priceType' | 'peakVolumeDiff' | 'quantity'>
) => {
  switch (item.priceType) {
    case 'T':
      return `${Math.round(item.quantity)} qty - ${formatTime(item.peakVolumeDiff)}`
    case 'D':
      return `${bytesToMegaBytes(item.peakVolumeDiff)} MB`
    case 'Q':
    case 'O':
    case 'S':
      return String(Math.round(item.quantity))
    default:
      return String(item.quantity)
  }
}

export const valuesForExport = (
  item: Pick<ReportEntityWithQuantity, 'priceType' | 'peakVolumeDiff' | 'quantity'>,
  type: 'quantity' | 'duration' | 'data'
) => {
  let val: string | number = 0

  switch (type) {
    case 'quantity':
      if (['T', 'Q', 'O', 'S'].includes(item.priceType)) {
        val = item.quantity
      }
      break
    case 'duration':
      if (item.priceType === 'T') {
        // Convert seconds to days
        val = item.peakVolumeDiff / 86400
      }
      break
    case 'data':
      if (item.priceType === 'D') {
        val = item.peakVolumeDiff
      }
      break
    default:
      val = item.quantity
  }

  return val
}
